// Pure helpers for the Modern Trade warehouse-tab lot form (spec 2026-11-03 §4):
// manual Lot / expiry entry with partial receipts. The backend
// (POST /sale-orders/:id/goods/manual) runs the same rules and has the final
// word; this module only keeps Save disabled until the form can pass.
// Vue-free so jest can cover it directly.

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad(n, w) {
  return String(n).padStart(w, "0");
}

// "YYYY-MM-DD" for a Date's local calendar day.
function isoOfLocalDate(d) {
  return `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1, 2)}-${pad(d.getDate(), 2)}`;
}

// Parse a strict "YYYY-MM-DD" (also accepts a Date). Returns {y, m, d} or null
// when the text is not a real calendar day (e.g. 2027-13-40, 2027-02-30).
function parseDay(v) {
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return null;
    return { y: v.getFullYear(), m: v.getMonth() + 1, d: v.getDate() };
  }
  const m = ISO_RE.exec(String(v == null ? "" : v).trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return null;
  return { y, m: mo, d };
}

// Calendar-year add with Go's time.AddDate(n, 0, 0) semantics: the day is kept
// and an out-of-range day rolls into the next month (2028-02-29 + 1y =
// 2029-03-01). Date.UTC normalises overflow the same way.
export function addYears(day, n) {
  const p = parseDay(day);
  if (!p) return "";
  const t = new Date(Date.UTC(p.y + n, p.m - 1, p.d));
  return `${pad(t.getUTCFullYear(), 4)}-${pad(t.getUTCMonth() + 1, 2)}-${pad(t.getUTCDate(), 2)}`;
}

// Earliest acceptable expiry for goods received on `today`.
export function minExpiry(today) {
  return addYears(today, 1);
}

// Positive whole number, or null.
function positiveInt(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  if (!isFinite(n) || !Number.isInteger(n) || n <= 0) return null;
  return n;
}

function normalizeDay(v) {
  const p = parseDay(v);
  return p ? `${pad(p.y, 4)}-${pad(p.m, 2)}-${pad(p.d, 2)}` : "";
}

// products: [{product_id, remaining}] (from GET /goods/progress)
// rows:     [{product_id, lot_no, expired_date: "YYYY-MM-DD", qty}]
// today:    receive date ("YYYY-MM-DD" or Date)
//
// Returns {rowErrors: {[rowIdx]: code}, productErrors: {[productId]: code},
// formError: "empty" | null, ok}. One code per row, first failing rule wins:
// blank_lot → blank_expiry → bad_qty → shelf_life_lt_1y → duplicate_row.
// A product with no rows this round is fine (partial receipt); only a form
// with no rows at all is `empty`.
export function validate(products, rows, today) {
  const list = rows || [];
  const rowErrors = {};
  const productErrors = {};
  const formError = list.length === 0 ? "empty" : null;
  const floor = minExpiry(today instanceof Date ? isoOfLocalDate(today) : today);

  const remaining = {};
  (products || []).forEach(p => {
    if (p) remaining[p.product_id] = Number(p.remaining) || 0;
  });

  const seen = new Set();
  const sums = {};
  list.forEach((r, idx) => {
    const lot = String((r && r.lot_no) || "").trim();
    const exp = normalizeDay(r && r.expired_date);
    const qty = positiveInt(r && r.qty);
    let code = null;
    if (!lot) code = "blank_lot";
    else if (!exp) code = "blank_expiry";
    else if (qty === null) code = "bad_qty";
    else if (!floor || exp < floor) code = "shelf_life_lt_1y";
    if (lot) {
      const key = `${r.product_id}\u0000${lot}`;
      if (seen.has(key) && !code) code = "duplicate_row";
      seen.add(key);
    }
    if (code) rowErrors[idx] = code;
    if (qty !== null) sums[r.product_id] = (sums[r.product_id] || 0) + qty;
  });

  Object.keys(sums).forEach(pid => {
    if (sums[pid] > (remaining[pid] || 0)) productErrors[pid] = "exceeds_remaining";
  });

  const ok = !formError && Object.keys(rowErrors).length === 0 && Object.keys(productErrors).length === 0;
  return { rowErrors, productErrors, formError, ok };
}

// Badge data from GET /goods/progress: "รับบางส่วน x/y" (partial) or
// "รับครบ" (full); none before the first receipt.
export function receiveProgress(progress) {
  let ordered = 0;
  let received = 0;
  let remaining = 0;
  (progress || []).forEach(p => {
    if (!p) return;
    ordered += Number(p.ordered) || 0;
    received += Number(p.received) || 0;
    remaining += Number(p.remaining) || 0;
  });
  let state = "none";
  if (ordered > 0 && remaining <= 0) state = "full";
  else if (received > 0) state = "partial";
  return { state, received, ordered, remaining };
}

// Whether the lot form / Excel wizard is open for an SO status. With progress
// rows (GET /goods/progress) receiving is open at confirmed(2), in production(3),
// goods received(4) and delivered(5) while anything remains: the factory may
// deliver in parts, and a delivery note for the first part moves the SO to 5.
// closed(6) / cancelled(9) never. Without progress (null = not loaded / old BE)
// keep the old 2-3 rule.
export function receiveOpen(status, progress) {
  const st = Number(status);
  if (progress === null || progress === undefined) return st >= 2 && st <= 3;
  return st >= 2 && st <= 5 && receiveProgress(progress).remaining > 0;
}

// POST /sale-orders/:id/goods/manual body. Line order = form row order, so the
// server's 1-based row_no points back at rows[row_no - 1].
export function manualPayload(rows) {
  return {
    lines: (rows || []).map(r => ({
      product_id: r.product_id,
      lot_no: String(r.lot_no || "").trim(),
      expired_date: normalizeDay(r.expired_date),
      qty: Number(r.qty),
    })),
  };
}

// 400 → {[rowIdx]: code}. MtGoodsManual puts the row list in Datum via
// r.Set({errors: [{row_no, code}]}). Returns null when the response carries no
// row errors (caller shows the generic message).
export function rowErrorsFromServer(data, rowCount) {
  const d = data && data.Datum;
  const errs = d && Array.isArray(d.errors) ? d.errors : null;
  if (!errs) return null;
  const out = {};
  errs.forEach(e => {
    const i = Number(e && e.row_no) - 1;
    if (Number.isInteger(i) && i >= 0 && i < rowCount && !(i in out)) out[i] = e.code;
  });
  return out;
}

// GET /goods/progress body → rows. MtGoodsProgress r.Append's each row, so
// Data is the bare row array (absent when there are none).
export function progressRows(body) {
  return body && Array.isArray(body.Data) ? body.Data : [];
}

// BE goods row code (MT-GoodsManual.go mtGoodsErr*: manual form 400 errors and
// the Excel wizard's per-row error_msg) → i18n key. Written out in full so
// i18n checks can grep every key. null = not a known code (show it raw).
export function lotErrorKey(code) {
  switch (code) {
    case "empty": return "modern_trade.lot_form.err_empty";
    case "company_inactive": return "modern_trade.lot_form.err_company_inactive";
    case "blank_lot": return "modern_trade.lot_form.err_blank_lot";
    case "blank_expiry": return "modern_trade.lot_form.err_blank_expiry";
    case "bad_date": return "modern_trade.lot_form.err_bad_date";
    case "bad_qty": return "modern_trade.lot_form.err_bad_qty";
    case "unknown_product": return "modern_trade.lot_form.err_unknown_product";
    case "not_in_sale_order": return "modern_trade.lot_form.err_not_in_sale_order";
    case "no_wms_mapping": return "modern_trade.lot_form.err_no_wms_mapping";
    case "shelf_life_lt_1y": return "modern_trade.lot_form.err_shelf_life_lt_1y";
    case "lot_expiry_mismatch": return "modern_trade.lot_form.err_lot_expiry_mismatch";
    case "duplicate_row": return "modern_trade.lot_form.err_duplicate_row";
    case "exceeds_remaining": return "modern_trade.lot_form.err_exceeds_remaining";
    default: return null;
  }
}
